import * as Sentry from "@sentry/angular";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LOCALE_ID, NgModule, isDevMode, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import localeDECH from "@angular/common/locales/de-CH";
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthenticationInterceptor } from './shared/interceptors/authentication.interceptor';
import { ErrorCatchingInterceptor } from './shared/interceptors/error-catching.interceptor';
import { SharedModule } from './shared/shared.module';
import { AuthenticationModule } from './authentication/authentication.module';
import { BasketModule } from './basket/basket.module';
import { OrdersModule } from './orders/orders.module';
import { ProductsModule } from './products/products.module';
import { CompanyModule } from './company/company.module';
import { TranslocoRootModule } from './transloco-root.module';
import { registerLocaleData } from '@angular/common';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { ProductSearchModule } from './product-search/product-search.module';
import { LanguageInterceptor } from './shared/interceptors/language.interceptor';
import { SettingsModule } from './settings/settings.module';
import { DevicesModule } from './devices/devices.module';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EmployeesEffects } from './stores/global/employees/employee-effects';
import { HistoryModule } from './history/history.module';
import { InstallationModule } from './installations/installations.module';
import { RevisionsModule } from './revisions/revisions.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslocoPreloadService } from './shared/services/transloco-preload-service';
import { Translation } from '@jsverse/transloco';
import { Router } from '@angular/router';
import { currentEmployeeReducers } from "./stores/global/current-employee/current-employee-reducers";
import { LoadingIndicationInterceptor } from "./shared/interceptors/loading-indication.interceptor";


registerLocaleData(localeDECH);

export function preloadTranslations(preloadService: TranslocoPreloadService): () => Promise<Translation> {
    return () => preloadService.preloadTranslations();
  }

@NgModule({ declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent], 
    imports: [
        BrowserModule,
        AppRoutingModule,
        SharedModule,
        AuthenticationModule,
        BasketModule,
        OrdersModule,
        ProductsModule,
        CompanyModule,
        DevicesModule,
        SettingsModule,
        HistoryModule,
        TranslocoRootModule,
        ProductSearchModule,
        InstallationModule,
        RevisionsModule,
        StoreModule.forFeature('currentEmployee', currentEmployeeReducers),
        EffectsModule.forFeature([EmployeesEffects]),
        StoreModule.forRoot({}),
        EffectsModule.forRoot(),
        StoreDevtoolsModule.instrument({
            maxAge: 25,
            logOnly: !isDevMode(),
            autoPause: true,
          }),
        ServiceWorkerModule.register('/ngsw-worker.js', {
            enabled: environment.production,

            registrationStrategy: 'registerWhenStable:30000'
        }),
        StoreModule.forRoot({}, {}),
        EffectsModule.forRoot([]),
        StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() }),
        BrowserAnimationsModule], 
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthenticationInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorCatchingInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LanguageInterceptor,
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: preloadTranslations,
            deps: [TranslocoPreloadService],
            multi: true,
          },
        { 
            provide: LOCALE_ID, 
            useValue: "de-CH" 
        },
            provideHttpClient(withInterceptorsFromDi()),
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoadingIndicationInterceptor,
            multi: true
        }, 
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
    ]
})
export class AppModule { }
